/**
 * navigation/appShell.tsx
 * The signed-in catalog shell: docked sidebar (overlay drawer on mobile),
 * topbar, command palette, and the content area — which hosts the app
 * navigator (appStack). The sidebar/palette navigate via the container ref
 * (navigationRef) since they render outside the navigator.
 */

import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Platform, TouchableOpacity } from 'react-native';

import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import Sidebar from '../shell/sidebar';
import Topbar from '../shell/topbar';
import CommandPalette from '../shell/commandPalette';
import AppStack from './appStack';
import { useActiveView } from './useActiveView';

interface AppShellProps {
  /** Sign out → root navigator returns to the auth stack. */
  onSignOut: () => void;
}

export default function AppShell({ onSignOut }: AppShellProps) {
  const { theme } = useTheme();
  const { colors } = theme;
  const [paletteOpen, setPaletteOpen] = useState(false);

  // The Site screen renders edge-to-edge (no content padding).
  const isSite = useActiveView() === 'site';

  // Below the mobile breakpoint the docked sidebar would crush the content, so
  // we switch to a hamburger-triggered overlay drawer (phones + small tablets).
  const { isMobile } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => { if (!isMobile && drawerOpen) setDrawerOpen(false); }, [isMobile, drawerOpen]);

  // Cmd+K on web
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <View style={[styles.app, { backgroundColor: colors.paper }]}>
      {/* Docked sidebar — wide screens only. */}
      {!isMobile && <Sidebar onLogout={onSignOut} />}

      <View style={styles.main}>
        <Topbar showMenu={isMobile} onMenu={() => setDrawerOpen(true)} onSignOut={onSignOut} />
        <View style={[styles.content, { backgroundColor: colors.paper }, isSite && styles.contentNoPad]}>
          <AppStack />
        </View>
      </View>

      {/* Mobile overlay drawer — sidebar slides over the content. */}
      {isMobile && drawerOpen && (
        <View style={styles.overlay}>
          <Sidebar
            mobile
            onLogout={() => { setDrawerOpen(false); onSignOut(); }}
            onNavigate={() => setDrawerOpen(false)}
          />
          <TouchableOpacity
            style={styles.scrim}
            activeOpacity={1}
            onPress={() => setDrawerOpen(false)}
            accessibilityLabel="Close menu"
          />
        </View>
      )}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  app: {
    flex: 1,
    flexDirection: 'row',
    ...(Platform.OS === 'web' ? { height: '100vh' as any, overflow: 'hidden' as any } : { flex: 1 }),
  },
  main: {
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    ...(Platform.OS === 'web' ? { overflow: 'hidden' as any } : {}),
  },
  content: {
    flex: 1,
    ...(Platform.OS === 'web' ? { overflowY: 'auto' as any } : {}),
  },
  contentNoPad: { padding: 0 },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    flexDirection: 'row',
    ...(Platform.OS === 'web' ? { zIndex: 100 as any } : {}),
  },
  scrim: { flex: 1, backgroundColor: 'rgba(15,23,42,0.45)' },
});
